<template>
  <div
    v-if="modelValue"
    class="pic-cropper-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Crop profile image"
  >
    <div class="pic-cropper-dialog">
      <div class="pic-cropper-header">
        <h5 class="pic-cropper-title">
          <i class="bi bi-person-circle me-2"></i>Set Profile Image
        </h5>
        <button type="button" class="btn-close" @click="cancel" aria-label="Close"></button>
      </div>

      <div class="pic-cropper-body">
        <div
          class="pic-cropper-viewport"
          ref="viewport"
          @mousedown.prevent="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          @wheel.prevent="onWheel"
          @touchstart.prevent="startTouchDrag"
          @touchmove.prevent="onTouchDrag"
          @touchend="stopDrag"
        >
          <canvas
            ref="previewCanvas"
            :width="CROP_SIZE"
            :height="CROP_SIZE"
            class="pic-cropper-canvas"
          ></canvas>
          <!-- Circular guide overlay -->
          <div class="pic-cropper-circle-guide" :style="circleGuideStyle"></div>
        </div>

        <div class="pic-cropper-zoom-row">
          <i class="bi bi-zoom-out pic-zoom-icon"></i>
          <input
            type="range"
            class="form-range pic-zoom-range"
            :min="minZoom"
            :max="maxZoom"
            step="0.005"
            :value="zoom"
            @input="onZoomSlider"
          />
          <i class="bi bi-zoom-in pic-zoom-icon"></i>
        </div>

        <p class="pic-cropper-hint">
          <i class="bi bi-arrows-move me-1"></i>Drag to reposition &nbsp;·&nbsp;
          <i class="bi bi-search me-1"></i>Scroll or slide to zoom
        </p>
      </div>

      <div class="pic-cropper-footer">
        <button type="button" class="btn btn-secondary btn-sm" @click="cancel">
          Cancel
        </button>
        <button type="button" class="btn btn-primary btn-sm" @click="confirm">
          <i class="bi bi-check-lg me-1"></i>Apply
        </button>
      </div>
    </div>

    <!-- Hidden output canvas — used only during confirm export -->
    <canvas ref="outputCanvas" style="display:none"></canvas>
  </div>
</template>

<script>
export default {
  name: 'ProfileImageCropper',

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    imageSrc: {
      type: String,
      default: null
    }
  },

  emits: ['update:modelValue', 'cropped', 'cancel'],

  data() {
    return {
      CROP_SIZE: 320,
      zoom: 1,
      minZoom: 0.1,
      maxZoom: 4,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      image: null
    }
  },

  computed: {
    circleGuideStyle() {
      const s = this.CROP_SIZE
      const radius = s / 2
      return {
        width: `${s}px`,
        height: `${s}px`,
        borderRadius: '50%',
        boxShadow: `0 0 0 ${s}px rgba(0, 0, 0, 0.55)`,
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        outline: '2px solid rgba(255,255,255,0.6)'
      }
    }
  },

  watch: {
    imageSrc(src) {
      if (src) this.loadImage(src)
    },
    modelValue(open) {
      if (open && this.imageSrc) this.loadImage(this.imageSrc)
    }
  },

  methods: {
    loadImage(src) {
      const img = new Image()
      img.onload = () => {
        this.image = img

        // Fit image to fill the crop circle (cover behaviour)
        const fitScale = Math.max(
          this.CROP_SIZE / img.naturalWidth,
          this.CROP_SIZE / img.naturalHeight
        )
        this.minZoom = fitScale
        this.maxZoom = fitScale * 4
        this.zoom = fitScale
        this.offsetX = 0
        this.offsetY = 0

        this.$nextTick(() => this.draw())
      }
      img.src = src
    },

    draw() {
      const canvas = this.$refs.previewCanvas
      if (!canvas || !this.image) return

      const ctx = canvas.getContext('2d')
      const s = this.CROP_SIZE

      ctx.clearRect(0, 0, s, s)

      const scaledW = this.image.naturalWidth * this.zoom
      const scaledH = this.image.naturalHeight * this.zoom
      const drawX = s / 2 - scaledW / 2 + this.offsetX
      const drawY = s / 2 - scaledH / 2 + this.offsetY

      ctx.drawImage(this.image, drawX, drawY, scaledW, scaledH)
    },

    clampOffset() {
      if (!this.image) return
      const s = this.CROP_SIZE
      const scaledW = this.image.naturalWidth * this.zoom
      const scaledH = this.image.naturalHeight * this.zoom

      // Allow dragging up to where the edge of the image meets the crop centre
      const maxOffX = Math.max(0, scaledW / 2 - s / 2)
      const maxOffY = Math.max(0, scaledH / 2 - s / 2)

      // When image is smaller than canvas allow full free movement
      const freeX = scaledW < s ? s / 2 : maxOffX
      const freeY = scaledH < s ? s / 2 : maxOffY

      this.offsetX = Math.max(-freeX, Math.min(freeX, this.offsetX))
      this.offsetY = Math.max(-freeY, Math.min(freeY, this.offsetY))
    },

    // ── Mouse events ─────────────────────────────────────────────────────────
    startDrag(e) {
      this.dragging = true
      this.lastMouseX = e.clientX
      this.lastMouseY = e.clientY
    },

    onDrag(e) {
      if (!this.dragging) return
      this.offsetX += e.clientX - this.lastMouseX
      this.offsetY += e.clientY - this.lastMouseY
      this.lastMouseX = e.clientX
      this.lastMouseY = e.clientY
      this.clampOffset()
      this.draw()
    },

    stopDrag() {
      this.dragging = false
    },

    onWheel(e) {
      const delta = e.deltaY < 0 ? 0.06 : -0.06
      this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom + delta))
      this.clampOffset()
      this.draw()
    },

    onZoomSlider(e) {
      this.zoom = parseFloat(e.target.value)
      this.clampOffset()
      this.draw()
    },

    // ── Touch events ─────────────────────────────────────────────────────────
    startTouchDrag(e) {
      if (e.touches.length === 1) {
        this.dragging = true
        this.lastMouseX = e.touches[0].clientX
        this.lastMouseY = e.touches[0].clientY
      }
    },

    onTouchDrag(e) {
      if (!this.dragging || e.touches.length !== 1) return
      this.offsetX += e.touches[0].clientX - this.lastMouseX
      this.offsetY += e.touches[0].clientY - this.lastMouseY
      this.lastMouseX = e.touches[0].clientX
      this.lastMouseY = e.touches[0].clientY
      this.clampOffset()
      this.draw()
    },

    // ── Output ────────────────────────────────────────────────────────────────
    confirm() {
      const out = this.$refs.outputCanvas
      const s = this.CROP_SIZE
      out.width = s
      out.height = s

      const ctx = out.getContext('2d')
      ctx.clearRect(0, 0, s, s)

      // Circular clip
      ctx.beginPath()
      ctx.arc(s / 2, s / 2, s / 2, 0, Math.PI * 2)
      ctx.clip()

      const scaledW = this.image.naturalWidth * this.zoom
      const scaledH = this.image.naturalHeight * this.zoom
      const drawX = s / 2 - scaledW / 2 + this.offsetX
      const drawY = s / 2 - scaledH / 2 + this.offsetY

      ctx.drawImage(this.image, drawX, drawY, scaledW, scaledH)

      const base64 = out.toDataURL('image/png')
      this.$emit('cropped', base64)
      this.$emit('update:modelValue', false)
    },

    cancel() {
      this.$emit('cancel')
      this.$emit('update:modelValue', false)
    }
  }
}
</script>

<style scoped>
.pic-cropper-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pic-cropper-dialog {
  background: #fff;
  border-radius: 12px;
  width: 380px;
  max-width: 95vw;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.pic-cropper-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.pic-cropper-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #343a40;
}

.pic-cropper-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pic-cropper-viewport {
  position: relative;
  width: 320px;
  height: 320px;
  overflow: hidden;
  background: #111;
  cursor: grab;
  user-select: none;
  border-radius: 4px;
}

.pic-cropper-viewport:active {
  cursor: grabbing;
}

.pic-cropper-canvas {
  display: block;
}

.pic-cropper-circle-guide {
  /* circleGuideStyle is bound inline */
}

.pic-cropper-zoom-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.pic-zoom-range {
  width: 200px;
}

.pic-zoom-icon {
  font-size: 1.1rem;
  color: #6c757d;
}

.pic-cropper-hint {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 8px;
  margin-bottom: 0;
  text-align: center;
}

.pic-cropper-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}
</style>
